import { forwardRef } from "react";
import { Box, Button, Grid2, Tooltip, Typography } from "@mui/material";
import { FilterList, Group, PersonAdd, SearchOutlined, Update } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { PricingModel } from "../../../features/Login/typings/login.types";
import {
  BookingType,
  NotSerializedBookingMode
} from "../../../features/Booking-Form/typings/booking-inputs";
import { ColleagueBookingFilters } from "./ColleagueBookingFilter.component";
import { ColleagueFilterState, GroupFilterSelected } from "./types/GroupFilters.type";
import { FloorAvailability } from "../../../features/FloorManager/typings/floor-inventory.entity";
import { ConnectedBookingData } from "../../../features/Booking-Form/typings/connected-bookingData";
import { genConnectedBookingData } from "./functions/BookingFilterButtonGroup.functions";

type P = {
  floorButtonLabel: string;
  bookingMode: NotSerializedBookingMode;
  floorAvailability: FloorAvailability;
  filterItems: GroupFilterSelected[];
  colleagueFilterState: ColleagueFilterState;
  selectedColleagueFilter: ConnectedBookingData[];
  isFilterEntryEmpty: boolean;
  isFilterAble: boolean;
  setIsFloorFilterOpen: () => void;
  setIsGroupFilterOpen: () => void;
  setColleagueFilterState: () => void;
  setIsDateOpen: () => void;
  setIsTeamBookingOpen: (o: boolean) => void;
  setSelectedColleagueFilter: (filter: ConnectedBookingData[]) => void;
};

export const BookingFilterButtons: React.FC<P> = forwardRef<HTMLDivElement, P>((props, ref) => {
  const {
    floorButtonLabel,
    bookingMode,
    floorAvailability,
    colleagueFilterState,
    selectedColleagueFilter,
    isFilterEntryEmpty,
    isFilterAble,
    setIsFloorFilterOpen,
    setIsGroupFilterOpen,
    setColleagueFilterState,
    setIsDateOpen,
    setIsTeamBookingOpen,
    setSelectedColleagueFilter
  } = props;

  const { t } = useTranslation();
  const { userInformation } = useSelector((state: RootState) => state.login);

  const uniqConnectedBookingData = genConnectedBookingData(
    floorAvailability?.connectedBookingData ?? []
  );

  return (
    <div ref={ref}>
      <Box
        sx={{ flexGrow: 1, mt: "2px", mb: "10px" }}
        data-testid="booking-filter-button-group-box"
      >
        <Grid2 container spacing={2} data-testid="booking-filter-button-group-grid-parent">
          {/* First grid item with buttons */}
          <Grid2 sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* floor button */}
            <Button
              data-testid="booking-filter-floor-btn"
              onClick={setIsFloorFilterOpen}
              disabled={!isFilterAble}
              sx={{ height: 40 }}
            >
              {floorButtonLabel}
            </Button>

            {/* group-filter button */}
            <Tooltip
              title={
                <Typography>
                  {isFilterEntryEmpty ? t("There are no available filter items") : t("Filter")}
                </Typography>
              }
              aria-label={
                isFilterEntryEmpty ? t("There are no available filter items") : t("Filter")
              }
            >
              <span>
                <Button
                  data-testid="booking-filter-group-filter-btn"
                  onClick={setIsGroupFilterOpen}
                  disabled={isFilterEntryEmpty}
                  sx={{ height: 40 }}
                >
                  <FilterList sx={{ mr: 1 }} />
                  {t("Filter")}
                </Button>
              </span>
            </Tooltip>

            {/* additional button - change time */}
            {userInformation.pricingModels.includes(PricingModel.ENTERPRISE) && (
              <Button
                sx={{ height: 40 }}
                data-testid="change-time"
                onClick={setIsDateOpen}
                aria-label="change time"
                color={"primary"}
                size="large"
                disabled={!isFilterAble}
              >
                <Update />
              </Button>
            )}
            {/* additional button - team booking mode */}
            {bookingMode === "team" && (
              <Tooltip
                title={<Typography>{t("Edit your team selection")}</Typography>}
                aria-label="Edit your team selection"
              >
                <span>
                  <Button
                    sx={{ height: 40 }}
                    data-testid="change-team-members"
                    onClick={() => setIsTeamBookingOpen(true)}
                    aria-label="change members"
                    color={"primary"}
                    size="large"
                    disabled={!isFilterAble}
                  >
                    <PersonAdd />
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* find colleagues button */}
            {bookingMode != BookingType.CONFERENCEZONE && (
              <Tooltip
                title={<Typography>{t("Find your colleagues")}</Typography>}
                aria-label="Find your colleagues"
              >
                <span>
                  <Button
                    data-testid="find-colleagues-btn"
                    sx={{ height: 40 }}
                    onClick={setColleagueFilterState}
                    color={
                      colleagueFilterState === ColleagueFilterState.DEACTIVATED
                        ? "error"
                        : "primary"
                    }
                    disabled={!isFilterAble}
                  >
                    {colleagueFilterState === ColleagueFilterState.DEACTIVATED ? (
                      <Group />
                    ) : (
                      <SearchOutlined />
                    )}
                    <Typography sx={{ pl: 1 }}>{uniqConnectedBookingData.length}</Typography>
                  </Button>
                </span>
              </Tooltip>
            )}
          </Grid2>

          {/* Second grid item with Autocomplete */}
          <Grid2 size={"grow"}>
            {/* find colleagues textField filter */}
            {floorAvailability && colleagueFilterState !== ColleagueFilterState.DEACTIVATED && (
              <ColleagueBookingFilters
                onClose={setColleagueFilterState}
                connectedBookingData={uniqConnectedBookingData}
                selectedColleagueFilter={selectedColleagueFilter}
                setSelectedColleagueFilter={setSelectedColleagueFilter}
                colleagueFilterState={colleagueFilterState}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>
    </div>
  );
});
